import { message } from "antd";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import db from "../../../../database";
import { SyncData } from "../Tables-Management/SyncData";
import CWKOTPrinter from "../../../../lib/printer/CWKotPrinter";
import { kotPrinter } from "../../../../lib/printer/kotPrinter";
import useRestaurantStore from "../../../../stores/useRestaurantStore";
import useAuthStore from "../../../../stores/useAuthStore";
import { stock } from "../../Core/stock";

const useCopyKOT = ({
  cart,
  setCart,
  recordState,
  posConfig,
  CWHardwareController,
  userPreferences,
  setOcupiedTablesData,
  setReservedTablesData,
  setTableCards,
  setTableList,
  setCopyKOTVisible,
  setSelectedCopyItems,
  setShowProducts,
  setKeyValue,
}) => {
  const pricePrecision = useAuthStore.getState().tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision ?? 2;

  const handleCopyKOTConfirm = async (selectedCopyItems) => {
    const selectedProductIds = Object.keys(selectedCopyItems).filter((productId) => selectedCopyItems[productId] > 0);

    if (selectedProductIds.length === 0) {
      message.warning("Please select at least one item to copy");
      return;
    }

    const newItems = selectedProductIds.map((productId) => {
      const originalItem = cart.items.find((i) => i.productId === productId);
      const selectedQty = selectedCopyItems[productId];
      const originalQty = originalItem.weight || 1;
      const unitNetTotal = (originalItem.nettotal || 0) / originalQty;
      const unitLineGross = (originalItem.linegross || 0) / originalQty;
      const unitLineNet = (originalItem.linenet || 0) / originalQty;
      const unitTax = (originalItem.taxRate || 0) / originalQty;

      return {
        ...originalItem,
        lineId: uuidv4().replace(/-/g, "").toUpperCase(),
        fbOrderLineId: uuidv4().replace(/-/g, "").toUpperCase(),
        weight: selectedQty,
        sendQty: selectedQty,
        firedQty: 0,
        order: "N",
        nettotal: parseFloat((unitNetTotal * selectedQty).toFixed(pricePrecision)),
        linegross: parseFloat((unitLineGross * selectedQty).toFixed(pricePrecision)),
        linenet: parseFloat((unitLineNet * selectedQty).toFixed(pricePrecision)),
        taxRate: parseFloat((unitTax * selectedQty).toFixed(pricePrecision)),
      };
    });

    const updatedCart = {
      ...cart,
      items: [...cart.items, ...newItems],
      total: cart.total + newItems.reduce((sum, item) => sum + item.nettotal, 0),
    };

    if (navigator.onLine) {
      const tillData = useAuthStore.getState().tillData;
      const stockCheckResult = await stock({
        cart: updatedCart,
        setCart,
        tillData,
        setProductStock: () => {},
        isFinalValidation: false,
        setTempCart: () => {},
        isCompleteSale: false,
        setShowCompleteSaleStock: () => {},
        setCompleteSaleStockData: () => {},
        customMixHandler: false,
        setProductBOMStock: () => {},
        setProductBOMStockData: () => {},
      });
      if (!stockCheckResult) {
        message.error("Insufficient stock to copy the selected item(s).");
        return;
      }
    }

    setCopyKOTVisible(false);
    setSelectedCopyItems({});

    const cartWithFiredItems = {
      ...updatedCart,
      items: updatedCart.items.map((cartItem) => {
        const isCopiedItem = newItems.some((i) => i.lineId === cartItem.lineId);
        return isCopiedItem ? { ...cartItem, order: "Y", firedQty: cartItem.weight } : cartItem;
      }),
    };

    setCart(cartWithFiredItems);
    await handleCopyKOTFire(newItems, cartWithFiredItems);
    message.success("Items copied and sent to kitchen successfully!");
  };

  const handleCopyKOTFire = async (copiedItems, updatedCart) => {
    try {
      let orderId = localStorage.getItem("orderId") === null ? 1 : Number(localStorage.getItem("orderId")) + 1;
      localStorage.setItem("orderId", orderId);

      let uniqId = uuidv4().replace(/-/g, "").toUpperCase();

      if (recordState) {
        recordState.uniqId = uniqId;
      }
      useRestaurantStore.getState().setTableName(recordState);

      let newCart = {
        ...updatedCart,
        tableNo: recordState?.table,
      };

      newCart?.items?.forEach((cartItem) => (cartItem.parkedItem = true));

      let orderLines = [];

      if (recordState?.cwrFbTableId) {
      const existingFbOrder = await db.fbOrderData
        .where("cwrFbTableId")
        .equals(recordState.cwrFbTableId)
        .filter((order) =>
          order.fbOrderStatus === "IP" &&
          (recordState.splitTable
            ? order.splitTable === recordState.splitTable
            : !order.splitTable)
        )
        .first();
        let fbOrder;
        let fbOrderId;

        if (existingFbOrder) {
          fbOrder = existingFbOrder;
          fbOrderId = existingFbOrder.fbOrderId;
        } else {
          const fbOrders = JSON.parse(localStorage.getItem("getFBOrderData") || "[]");
          const filteredOrders = Array.isArray(fbOrders)
            ? fbOrders.filter((o) => o.fbTableId === recordState?.cwrFbTableId || o.fbTableId === recordState?.fbTableId)
            : [];
          const fbOrderDataID = filteredOrders[0]?.fbOrderId;
          fbOrderId = fbOrderDataID || uuidv4().replace(/-/g, "").toUpperCase();
          fbOrder = {
            fbOrderId,
            cwrFbTableId: recordState?.cwrFbTableId,
            fbOrderStatus: "IP",
            lines: [],
            fbOrderSync: "N",
            cart: newCart,
            name: recordState?.table,
            noOfPersons: recordState?.noOfPersons,
            statusType: "OCU",
            dineIn: useRestaurantStore.getState().dineIn,
            uniqId: uniqId,
          };
        }

        await db.tableData
          .where("cwrFbTableId")
          .equals(recordState?.cwrFbTableId)
          .modify((eventValue) => {
            eventValue.items = newCart.items;
            eventValue.totalAmount = newCart.total;
            eventValue.documentno = newCart.documentno;
            eventValue.time = dayjs(new Date()).format(userPreferences.dateTimeFormat);
            eventValue.statusType = "ONG";
            eventValue.tableSync = "N";
          });

        copiedItems.forEach((item) => {
          let fbOrderLineId = item.fbOrderLineId || uuidv4().replace(/-/g, "").toUpperCase();
          let fbMetaOrderId = uuidv4().replace(/-/g, "").toUpperCase();
          const lineId = item.lineId;
          const usedKeysLower = new Set([
            "productid", "mbatchid", "description", "uom", "tax", "discount",
            "weight", "unitprice", "listprice", "linegross", "lineid", "grossstd",
            "name", "value", "grossunit", "linestd", "netlist", "netstd", "netunit",
            "unittax", "order",
          ]);

          const metaFromItem = Object.fromEntries(Object.entries(item).filter(([key]) => !usedKeysLower.has(key.toLowerCase())));
          const metaString = {
            ...metaFromItem,
            lineId,
            fbMetaOrderId,
            fbOrderLineId,
            isSelected: "N",
            order: "Y",
          };

          item.fbOrderLineId = fbOrderLineId;
          orderLines.push({
            fbOrderId: `${fbOrderId}`,
            fbOrderLineId: `${fbOrderLineId}`,
            lineId: lineId,
            mPoductId: `${item.productId || item.mPoductId}`,
            mBatchId: item.mBatchId,
            description: `${item.description || ""}`,
            csUomId: `${item.uom}`,
            csTaxId: `${item.tax}`,
            discount: item.discount,
            line: 1,
            qty: item.weight,
            unitPrice: item.unitPrice,
            listPrice: item.listPrice,
            lineNet: item.linenet,
            lineTax: item.taxRate,
            lineGross: item.linegross,
            sOrderLineId: null,
            isOrdered: "Y",
            isCanceled: "N",
            grossStd: item.grossstd,
            grossUnit: item.grossunit,
            lineStd: item.linestd,
            netList: item.netList,
            netStd: item.netStd,
            netUnit: item.netunit,
            unitTax: item.unitTax,
            meta: [
              {
                key: "orderLine",
                value: JSON.stringify(metaString),
                fbOrderLineMetaId: `${fbMetaOrderId}`,
              },
            ],
          });
        });

        let array1 = [];
        for (let i = 0; i < newCart.items.length; i++) {
          let item = newCart.items[i];
          let printer = item.productionCenter;
          if (!array1.hasOwnProperty(`${printer}`)) {
            array1[`${printer}`] = [];
          }
          array1[`${printer}`].push(newCart.items[i]);
        }

        if (posConfig?.enableKOTPrint === "Y") {
          if (CWHardwareController) {
            const filteredCart = { ...newCart, items: copiedItems };
            CWKOTPrinter(filteredCart);
          } else {
            kotPrinter(array1, fbOrder.fbOrderId);
          }
        }

        fbOrder.lines = orderLines;
        fbOrder.fbOrderSync = "N";
        fbOrder.cart = newCart;

        await db.fbOrderData.put(fbOrder, fbOrderId);
        await SyncData(fbOrder, "upsertFbOrder", () => {}, "", true, newCart);

        const updatedTableData = await db.tableData.toArray();
        const updatedTable = updatedTableData.find((t) => t.cwrFbTableId === recordState?.cwrFbTableId);

        if (updatedTable) {
          await SyncData(updatedTable, "upsertTableStatus");
        }

        setTableCards(updatedTableData);
        const ocuTables = updatedTableData.filter((table) => table.statusType === "OCU" || table.statusType === "ONG");
        setOcupiedTablesData(ocuTables);
        const resTables = updatedTableData.filter((table) => table.statusType === "RES");
        setReservedTablesData(resTables);

        let tempData = [];
        await db.tableData.toArray().then((fetched) => {
          tempData.push(...fetched);
        });
        setTableList(tempData);

        setTimeout(() => {
          setShowProducts(false);
          setKeyValue("0");
        }, 1500);
      }
    } catch (error) {
      console.error("[v0] Error in handleCopyKOTFire:", error);
      message.error("Failed to send items to kitchen");
    }
  };

  return { handleCopyKOTConfirm, handleCopyKOTFire };
};

export default useCopyKOT;
